import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LinkedInAuthorityContext = createContext(null);

export function LinkedInAuthorityProvider({ children }) {
  const { user } = useAuth();
  const [packsByProject, setPacksByProject] = useState({});
  const [limitsByProject, setLimitsByProject] = useState({});
  const [loading, setLoading] = useState(false);

  const refreshPacks = useCallback(async (projectId) => {
    if (!projectId || !user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/linkedin-authority-packs`);
      setPacksByProject((prev) => ({ ...prev, [projectId]: data.packs || [] }));
      setLimitsByProject((prev) => ({ ...prev, [projectId]: data.limits || {} }));
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createPack = useCallback(async (projectId, body) => {
    const { data } = await api.post(`/projects/${projectId}/linkedin-authority-packs`, body);
    await refreshPacks(projectId);
    return data;
  }, [refreshPacks]);

  const deletePack = useCallback(async (projectId, packId) => {
    await api.delete(`/linkedin-authority-packs/${packId}`);
    setPacksByProject((prev) => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter((p) => p.id !== packId),
    }));
  }, []);

  const value = useMemo(() => ({
    packsByProject, limitsByProject, loading, refreshPacks, createPack, deletePack,
  }), [packsByProject, limitsByProject, loading, refreshPacks, createPack, deletePack]);

  return <LinkedInAuthorityContext.Provider value={value}>{children}</LinkedInAuthorityContext.Provider>;
}

export function useLinkedInAuthority() {
  const ctx = useContext(LinkedInAuthorityContext);
  if (!ctx) throw new Error("useLinkedInAuthority must be inside LinkedInAuthorityProvider");
  return ctx;
}
